import { BLOCK_PREFIX, SLOT_CLASS } from "./draft_fill";

// Gaps between the blocks of his Word draft as the viewer draws it. Each block
// starts with a bookmark drawn as an id ("wf-b-<n>"), so a block split across
// pages is still one block, and a gap maps to the block it comes before.

// The blocks the viewer draws on each page.
const DRAWN_BLOCKS = "section.docx > article > *";
// How far into a block's text its gap reaches.
const INTO = 6;

export type Block = {
  element: Element;
  // The block of his file it draws, counted from 0.
  index: number;
  // The block's first (or last) drawn piece, where it meets the gap above (or below).
  first: boolean;
  last: boolean;
};

export type Gap = {
  // The block of his file it's before (DraftingSectionAddInput.before).
  before: number;
  // In the frame's viewport.
  top: number;
  left: number;
  width: number;
};

// Where a block's text starts and ends, which a paragraph's spacing in the
// viewer's drawing can sit outside of; its box when it has no text.
const textEdges = (element: Element) => {
  const range = element.ownerDocument.createRange();
  range.selectNodeContents(element);
  const lines = [...range.getClientRects()].filter(({ height }) => height > 0);
  const box = element.getBoundingClientRect();
  if (lines.length === 0) return { top: box.top, bottom: box.bottom };

  return {
    top: Math.min(...lines.map(({ top }) => top)),
    bottom: Math.max(...lines.map(({ bottom }) => bottom)),
  };
};

const indexOf = (element: Element) => {
  const id = element.querySelector(`[id^="${BLOCK_PREFIX}"]`)?.id;
  return id ? Number(id.slice(BLOCK_PREFIX.length)) : null;
};

// Every drawn block in reading order, each piece of a split one under its
// block's number. Read once a draft is drawn, never while he scrolls.
export const drawnBlocks = (root: Element): Block[] => {
  const blocks: Block[] = [];
  root.querySelectorAll(DRAWN_BLOCKS).forEach((element) => {
    const marked = indexOf(element);
    const previous = blocks.at(-1);
    const index = marked ?? previous?.index;
    if (index === undefined) return;

    const first = marked !== null;
    if (previous && previous.index === index) previous.last = false;
    blocks.push({ element, index, first, last: true });
  });
  return blocks;
};

// The gap before blocks[at]: midway between its text and the text above.
const gapBefore = (blocks: ReadonlyArray<Block>, at: number): Gap | null => {
  const block = blocks[at];
  if (!block?.first) return null;
  const rect = block.element.getBoundingClientRect();
  const text = textEdges(block.element);
  const above = blocks[at - 1] && textEdges(blocks[at - 1].element);
  const top = above ? (above.bottom + text.top) / 2 : text.top - 4;
  return { before: block.index, top, left: rect.left, width: rect.width };
};

// The gap after blocks[at]: midway between its text and the text below.
const gapAfter = (blocks: ReadonlyArray<Block>, at: number): Gap | null => {
  const block = blocks[at];
  if (!block?.last) return null;
  const rect = block.element.getBoundingClientRect();
  const text = textEdges(block.element);
  const below = blocks[at + 1] && textEdges(blocks[at + 1].element);
  const top = below ? (text.bottom + below.top) / 2 : text.bottom + 4;
  return { before: block.index + 1, top, left: rect.left, width: rect.width };
};

// The page's blocks on either side of the space `y` is in, when the pointer
// is on the page between them rather than on a block.
const between = (blocks: ReadonlyArray<Block>, page: Element, y: number) => {
  const at = blocks.findIndex(
    ({ element }) =>
      element.parentElement === page && element.getBoundingClientRect().top > y,
  );
  return at > 0 && blocks[at - 1]?.element.parentElement === page ? at : null;
};

const hasSlot = (block: Block | undefined) =>
  Boolean(block?.element.querySelector(`.${SLOT_CLASS}`));

// The gap the pointer is over: the space between one block's text and the
// next's, and INTO px of each. Tables, text boxes, a section's lines or block
// and page breaks inside a paragraph have none within them.
export const gapAt = (
  blocks: ReadonlyArray<Block>,
  target: Element,
  y: number,
): Gap | null => {
  const at = blocks.findIndex(({ element }) => element.contains(target));
  if (at === -1) {
    const next = between(blocks, target, y);
    if (next === null) return null;
    if (!hasSlot(blocks[next])) return gapBefore(blocks, next);
    return hasSlot(blocks[next - 1]) ? null : gapAfter(blocks, next - 1);
  }
  if (hasSlot(blocks[at])) return null;

  const text = textEdges(blocks[at].element);
  if (y < text.top + INTO) return gapBefore(blocks, at);
  if (y > text.bottom - INTO) return gapAfter(blocks, at);
  return null;
};
